import { MOBILE_PRINTER_NUMBER, MOBILE_PRINTER_LIST_FEATURE_ENABLED } from '../constants';

// Mobile Printer Test Connection API Error Codes
export const MOBILE_PRINTER_ERROR_CODE_INVALID_NUMBER = 4;
export const MOBILE_PRINTER_ERROR_CODE_PRINT_FAILED = 7;

export const getMobilePrinterNumber = () => {
  try {
    return localStorage.getItem(MOBILE_PRINTER_NUMBER) || '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting mobile printer number from storage:', error);
    return '';
  }
};

export const isMobilePrinterConnected = () => {
  return !!getMobilePrinterNumber();
};

export const removeMobilePrinterFromStorage = () => {
  try {
    localStorage.removeItem(MOBILE_PRINTER_NUMBER);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error removing mobile printer number from storage:', error);
  }
};

export const saveMobilePrinterToStorage = (printerNumber: string) => {
  try {
    localStorage.setItem(MOBILE_PRINTER_NUMBER, printerNumber);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving mobile printer number to storage:', error);
  }
};

export const saveMobilePrinterListFeatureEnabled = (status: string) => {
  try {
    localStorage.setItem(MOBILE_PRINTER_LIST_FEATURE_ENABLED, status);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving mobile printer feature enabled to storage:', error);
  }
};

export const getMobilePrinterListFeatureEnabled = (): boolean | null => {
  try {
    const value = localStorage.getItem(MOBILE_PRINTER_LIST_FEATURE_ENABLED);
    return value === null ? null : value === 'true';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting mobile printer feature enabled from storage:', error);
    return null;
  }
};

export const removeMobilePrinterListFeatureEnabled = () => {
  try {
    localStorage.removeItem(MOBILE_PRINTER_LIST_FEATURE_ENABLED);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error removing mobile printer feature enabled from storage:', error);
  }
};

/**
 * Parses mobile printer error code from the Test Connection API error message.
 * Expected format: "prefix: code-message"
 * Example: "413170702: 4-Invalid mobile printer number. Must be between 41 and 46..."
 *
 * @param message - The error message from the Test Connection API response
 * @returns The error code (1-7) or null if no valid code found
 */
export const parseMobilePrinterTestConnectionErrorCode = (message: string): number | null => {
  if (!message) return null;

  // Extract error code from format: "prefix: code-message"
  const match = message.match(/:\s*(\d+)-/);
  const code = match ? parseInt(match[1], 10) : null;

  // Only return codes 1-7 as per API specification
  if (code && code >= 1 && code <= 7) {
    return code;
  }

  return null;
};

/**
 * Returns the user-friendly error message for Mobile Printer Test Connection API errors
 * based on the error code returned from the Gemini Mobile Printer Test Connection API.
 *
 * Error codes:
 * - MOBILE_PRINTER_ERROR_CODE_INVALID_NUMBER (4): Invalid mobile printer number (must be 41-46)
 * - MOBILE_PRINTER_ERROR_CODE_PRINT_FAILED (7): Printer failed to print
 * - Other codes: Fallback to generic warning message
 *
 * @param errorCode - The error code (1-7) from the Test Connection API response
 * @param t - Translation function from i18next
 * @param translationRoot - The translation root key (e.g., 'MOBILE_PRINTER')
 * @returns The translated error message
 */
export const getMobilePrinterTestConnectionErrorMessage = (
  errorCode: number | null,
  t: (key: string) => string,
  translationRoot: string
): string => {
  if (errorCode === MOBILE_PRINTER_ERROR_CODE_INVALID_NUMBER) {
    return t(`${translationRoot}.TEST_PRINT_ERROR_CODE_4`);
  }
  if (errorCode === MOBILE_PRINTER_ERROR_CODE_PRINT_FAILED) {
    return t(`${translationRoot}.TEST_PRINT_ERROR_CODE_7`);
  }
  // Fallback for unknown error codes or communication errors
  return t(`${translationRoot}.TEST_PRINT_WARNING_FALLBACK`);
};

/**
 * Formats an AutoZoner's name for display on mobile printer test labels.
 * Returns "FirstName LastInitial." format (e.g., "Silpa P.")
 * Falls back to "Test" if firstName or lastName is null/undefined/empty.
 *
 * @param firstName - The AutoZoner's first name (from userData.givenName)
 * @param lastName - The AutoZoner's last name (from userData.sn)
 * @returns Formatted name string or "Test" as fallback
 */
export const formatNameForPrinterLabel = (
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string => {
  // Trim whitespace and check if values are valid
  const trimmedFirstName = firstName?.trim();
  const trimmedLastName = lastName?.trim();

  // Return "Test" if either name is null, undefined, or empty
  if (!trimmedFirstName || !trimmedLastName) {
    return 'Test';
  }

  // Format as "FirstName LastInitial."
  const lastInitial = trimmedLastName.charAt(0).toUpperCase();
  return `${trimmedFirstName} ${lastInitial}.`;
};
